import {
  Dumbbell,
  BookHeart,
  UserPen,
  LogOut,
  Crown,
  Users,
  HelpCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import PageTitleScript from "@/components/MetaScript";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { authApi } from "@/api/authApi";
import { useToastHandler } from "@/hooks/useToastHandler";
import { useUnreadBadge } from "@/hooks/useMates";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export function SettingsPage() {
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const { handleSuccess, handleError } = useToastHandler();
  const { hasUnread } = useUnreadBadge();

  const handleLogout = async () => {
    try {
      await authApi.logout();
      handleSuccess("Logged out successfully", 1000);
      setTimeout(() => {
        window.location.href = import.meta.env.VITE_MAIN_SITE;
      }, 300);
    } catch (error) {
      console.error("Error logging out:", error);
      handleError("Failed to log out");
    }
  };

  return (
    <div className="flex flex-col p-6 min-h-[60vh]">
      <PageTitleScript title="Account" />
      <h1 className="text-2xl font-bold mb-6 text-tx-dark">Account</h1>

      <div className="space-y-4 mb-12">
        <Link
          to="/settings/exercises"
          className="flex items-center justify-between p-4 bg-white rounded-xl shadow-xs"
        >
          <span className="font-medium text-tx-dark">Exercises</span>
          <div className="bg-gray-100 rounded-full p-2">
            <Dumbbell size={20} className="text-tx-dark" />
          </div>
        </Link>

        <Link
          to="/settings/mates"
          className="flex items-center justify-between p-4 bg-white rounded-xl shadow-xs relative"
        >
          <span className="font-medium text-tx-dark">Workout Mates</span>
          <div className="bg-gray-100 rounded-full p-2 relative">
            <Users size={20} className="text-tx-dark" />
            {hasUnread && (
              <span
                className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white animate-pulse"
                aria-hidden="true"
              />
            )}
          </div>
        </Link>

        {/* Upgrade Banner if `user.subscription.is_subscribed` is false */}
        <Link
          to="/settings/subscription"
          className="flex items-center justify-between p-4 bg-white rounded-xl shadow-xs"
        >
          <span className="font-bold text-accent-bg-hot">Pro Access</span>
          <div className="bg-accent-bg-hot rounded-full p-2">
            <Crown size={18} className="text-accent-fg-hot" />
          </div>
        </Link>

        <Link
          to="/settings/profile"
          className="flex items-center justify-between p-4 bg-white rounded-xl shadow-xs"
        >
          <span className="font-medium text-tx-dark">Profile</span>
          <div className="bg-gray-100 rounded-full p-2">
            <UserPen size={20} className="text-tx-dark" />
          </div>
        </Link>

        <Link
          to="/settings/preferences"
          className="flex items-center justify-between p-4 bg-white rounded-xl shadow-xs"
        >
          <span className="font-medium text-tx-dark">Preferences</span>
          <div className="bg-gray-100 rounded-full p-2">
            <BookHeart size={20} className="text-tx-dark" />
          </div>
        </Link>

        <Link
          to="/settings/help"
          className="flex items-center justify-between p-4 bg-white rounded-xl shadow-xs"
        >
          <span className="font-medium text-tx-dark">Help & Support</span>
          <div className="bg-gray-100 rounded-full p-2">
            <HelpCircle size={20} className="text-tx-dark" />
          </div>
        </Link>
      </div>

      <div className="container max-w-xs mx-auto">
        <div className="flex items-center justify-center">
          <p
            className="text-base text-center cursor-pointer text-tx-default"
            onClick={() => setShowSignOutDialog(true)}
          >
            Click here to{" "}
            <b className="underline underline-offset-4">sign out</b>
          </p>
        </div>
      </div>

      <Dialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
        <DialogContent className="p-6 border-0 overflow-hidden max-w-md bg-white">
          <DialogTitle className="text-2xl font-bold">Confirm</DialogTitle>
          <div className="border-t border-gray-200"></div>
          <DialogDescription className=" text-tx-default text-base mb-5">
            Are you sure you want to sign out of your account?
          </DialogDescription>

          <div className="flex justify-between space-x-3">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setShowSignOutDialog(false)}
              className="border-gray-300 "
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="lg"
              className="flex-1"
              onClick={() => {
                setShowSignOutDialog(false);
                handleLogout();
              }}
            >
              <LogOut className="h-5 w-5 mr-2" />
              Sign Out
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
